import React, { useEffect, useState } from 'react';
import { Input } from 'rsuite';
import { Button, Modal } from '@nsi-advisory-services/components';

// Title and notes for a saved run, plus the one destructive thing that can be
// done to it.
//
// A run is saved the moment its hours are pulled, so nobody is asked to name it
// up front. This is where it gets a name if it needs one - and where it gets
// deleted, which is the only way to run a period again.
//
// Part of the company-specific payroll module; see config/payrollFeature.js.
export default function PayrollRunInfoModal({
  open, run, periodLabel, onClose, onSave, onDelete, saving, deleting,
}) {
  const [name, setName] = useState('');
  const [notes, setNotes] = useState('');
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(run?.name || '');
    setNotes(run?.notes || '');
    setConfirmingDelete(false);
  }, [open, run]);

  const dirty = (run?.name || '') !== name.trim() || (run?.notes || '') !== notes.trim();

  return (
    <Modal open={open} onClose={onClose} size="sm">
      <Modal.Header>
        <Modal.Title>Run information</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div className="bdc-modal__label">Title</div>
        <Input
          value={name}
          onChange={setName}
          placeholder={periodLabel}
          disabled={deleting}
        />
        <div className="payroll-runinfo__hint">
          Leave empty to identify the run by its pay period.
        </div>

        <div className="bdc-modal__label" style={{ marginTop: 16 }}>Notes</div>
        <Input
          as="textarea"
          rows={5}
          value={notes}
          onChange={setNotes}
          placeholder="Why it was re-run, what was checked, anything worth knowing later."
          disabled={deleting}
        />

        {confirmingDelete ? (
          <div className="payroll-runinfo__danger">
            <i className="bi bi-exclamation-triangle" aria-hidden="true" />
            <div>
              <strong>Delete this run?</strong> The figures are gone for good, along with the corrections and
              loan repayments entered on it. The period can then be pulled again from scratch.
            </div>
          </div>
        ) : null}
      </Modal.Body>

      {/* Destructive action sits at the far end, away from Save, and asks
          once before it does anything. */}
      <Modal.Footer
        left={confirmingDelete ? (
          <Button variant="danger-solid" icon="trash" onClick={onDelete} loading={deleting}>
            Yes, delete it
          </Button>
        ) : (
          <Button variant="danger" icon="trash" onClick={() => setConfirmingDelete(true)} disabled={saving}>
            Delete run
          </Button>
        )}
      >
        <Button
          onClick={confirmingDelete ? () => setConfirmingDelete(false) : onClose}
          disabled={saving || deleting}
        >
          Cancel
        </Button>

        {confirmingDelete ? null : (
          <Button variant="primary" onClick={() => onSave({ name, notes })} loading={saving} disabled={!dirty}>
            Save
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
}
